// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

/**
 * Persistence for the unsent feedback draft's attachments.
 *
 * Files cannot live in localStorage (quota, serialisation), so picked images
 * go to IndexedDB; the text fields stay in localStorage next to the rest of
 * the draft (see useFeedbackDraft.ts). Everything here is best-effort: a
 * blocked or full store must never break the feedback form itself.
 */

const DB_NAME = 'flaircv-feedback';
const DB_VERSION = 1;
const STORE = 'attachments';
/** Pre-scoping key, deleted on sight so old global drafts cannot leak across
 *  accounts. */
const LEGACY_KEY = '***';

/** Attachments are scoped per user: a shared browser must never show one
 *  account's unsent draft (message, files, and reply-to email) to another. */
export function pendingKey(scope: string): string {
  return `pending:${scope}`;
}

function idbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Stores the current attachment list for one user. `isCurrent` is checked
 * again once the database is open, so a queued write cannot resurrect a
 * draft the user has cancelled in the meantime.
 */
export async function saveFeedbackFiles(
  scope: string,
  files: File[],
  isCurrent?: () => boolean,
): Promise<void> {
  if (!idbAvailable() || !scope) return;
  try {
    const db = await openDB();
    try {
      if (isCurrent && !isCurrent()) return;
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        const key = pendingKey(scope);
        if (files.length === 0) {
          tx.objectStore(STORE).delete(key);
        } else {
          tx.objectStore(STORE).put(files, key);
        }
        tx.objectStore(STORE).delete(LEGACY_KEY);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } finally {
      db.close();
    }
  } catch {
    // best-effort
  }
}

export async function loadFeedbackFiles(scope: string): Promise<File[]> {
  if (!idbAvailable() || !scope) return [];
  try {
    const db = await openDB();
    try {
      const stored = await new Promise<unknown>((resolve, reject) => {
        // readwrite so the legacy unscoped record can be purged while we are
        // in here; it must not linger on a shared browser.
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).delete(LEGACY_KEY);
        const req = tx.objectStore(STORE).get(pendingKey(scope));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
      return Array.isArray(stored) ? (stored as File[]) : [];
    } finally {
      db.close();
    }
  } catch {
    return [];
  }
}

export async function clearFeedbackFiles(scope: string): Promise<void> {
  await saveFeedbackFiles(scope, []);
}
